import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock, ArrowRight, Filter, Award } from 'lucide-react'

const GuideListPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedDifficulty, setSelectedDifficulty] = useState('all')

  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'levels', name: 'Level Guides' },
    { id: 'basics', name: 'Basics' },
    { id: 'conversation', name: 'Conversation' },
    { id: 'pronunciation', name: 'Pronunciation' },
    { id: 'grammar', name: 'Grammar' },
    { id: 'business', name: 'Business English' },
    { id: 'advanced', name: 'Advanced Topics' }
  ]

  const difficulties = [
    { id: 'all', name: 'All Levels' },
    { id: 'beginner', name: 'Beginner' },
    { id: 'intermediate', name: 'Intermediate' },
    { id: 'advanced', name: 'Advanced' }
  ]

  const levelGuides = [
    {
      title: 'A1 Level Guide (Beginner)',
      description: 'Basic English for immediate needs and familiar situations',
      category: 'levels',
      difficulty: 'beginner',
      readTime: 'Interactive',
      slug: 'a1',
      featured: true,
      isLevel: true,
      color: 'from-green-400 to-emerald-500'
    },
    {
      title: 'A2 Level Guide (Elementary)',
      description: 'Simple information exchange in familiar contexts',
      category: 'levels',
      difficulty: 'beginner',
      readTime: 'Interactive',
      slug: 'a2',
      featured: true,
      isLevel: true,
      color: 'from-blue-400 to-cyan-500'
    },
    {
      title: 'B1 Level Guide (Intermediate)',
      description: 'Independent language use for daily life situations',
      category: 'levels',
      difficulty: 'intermediate',
      readTime: 'Interactive',
      slug: 'b1',
      featured: true,
      isLevel: true,
      color: 'from-yellow-400 to-orange-500'
    },
    {
      title: 'B2 Level Guide (Upper-Intermediate)',
      description: 'Complex communication with fluency and spontaneity',
      category: 'levels',
      difficulty: 'intermediate',
      readTime: 'Interactive',
      slug: 'b2',
      featured: true,
      isLevel: true,
      color: 'from-purple-400 to-pink-500'
    },
    {
      title: 'C1 Level Guide (Advanced)',
      description: 'Effective operational proficiency for complex purposes',
      category: 'levels',
      difficulty: 'advanced',
      readTime: 'Interactive',
      slug: 'c1',
      featured: true,
      isLevel: true,
      color: 'from-red-400 to-rose-500'
    },
    {
      title: 'C2 Level Guide (Mastery)',
      description: 'Near-native fluency for virtually any communicative situation',
      category: 'levels',
      difficulty: 'advanced',
      readTime: 'Interactive',
      slug: 'c2',
      featured: true,
      isLevel: true,
      color: 'from-indigo-400 to-purple-600'
    }
  ]

  const guides = [
    ...levelGuides,
    {
      title: 'First Conversation with AI',
      description: 'Step-by-step guide to having your first English conversation with AI',
      category: 'basics',
      difficulty: 'beginner',
      readTime: '5 min read',
      slug: 'first-conversation-with-ai',
      featured: true,
      isLevel: false
    },
    {
      title: 'Advanced Pronunciation Tips',
      description: 'Master difficult sounds and improve your accent with AI feedback',
      category: 'pronunciation',
      difficulty: 'advanced',
      readTime: '8 min read',
      slug: 'advanced-pronunciation-tips',
      featured: true,
      isLevel: false
    },
    {
      title: 'Business English Conversations',
      description: 'Practice professional English for workplace success',
      category: 'business',
      difficulty: 'intermediate',
      readTime: '12 min read',
      slug: 'business-english-conversations',
      featured: true,
      isLevel: false
    },
    {
      title: 'Common Interview Questions & Answers',
      description: 'Master 30 essential interview questions with professional sample answers',
      category: 'business',
      difficulty: 'intermediate',
      readTime: '15 min read',
      slug: 'common-interview-questions',
      featured: true,
      isLevel: false
    },
    {
      title: 'Daily Conversation Practice',
      description: 'Common phrases and expressions for everyday situations',
      category: 'conversation',
      difficulty: 'beginner',
      readTime: '6 min read',
      slug: 'daily-conversation-practice',
      featured: false,
      isLevel: false
    },
    {
      title: 'Grammar Correction with AI',
      description: 'How to use AI feedback to improve your grammar naturally',
      category: 'grammar',
      difficulty: 'intermediate',
      readTime: '7 min read',
      slug: 'grammar-correction-with-ai',
      featured: false,
      isLevel: false
    },
    {
      title: 'Confidence Building Techniques',
      description: 'Overcome speaking anxiety and build confidence with AI practice',
      category: 'basics',
      difficulty: 'beginner',
      readTime: '9 min read',
      slug: 'confidence-building-techniques',
      featured: false,
      isLevel: false
    },
    {
      title: 'Mastering English Idioms',
      description: 'Learn and practice common English idioms in context',
      category: 'advanced',
      difficulty: 'advanced',
      readTime: '10 min read',
      slug: 'mastering-english-idioms',
      featured: false,
      isLevel: false
    },
    {
      title: 'Phone Conversations in English',
      description: 'Practice telephone etiquette and professional phone calls',
      category: 'business',
      difficulty: 'intermediate',
      readTime: '8 min read',
      slug: 'phone-conversations-english',
      featured: false,
      isLevel: false
    }
  ]

  const filteredGuides = guides.filter(guide => {
    const categoryMatch = selectedCategory === 'all' || guide.category === selectedCategory
    const difficultyMatch = selectedDifficulty === 'all' || guide.difficulty === selectedDifficulty
    return categoryMatch && difficultyMatch
  })

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-500/20 text-green-400'
      case 'intermediate':
        return 'bg-yellow-500/20 text-yellow-400'
      case 'advanced':
        return 'bg-red-500/20 text-red-400'
      default:
        return 'bg-slate-500/20 text-slate-400'
    }
  }

  return (
    <div className="px-4 py-12 sm:px-6 lg:px-8 animate-fade-in">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            <span className="gradient-text">English Speaking</span> Guides
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Comprehensive guides to help you master English conversation with AI assistance.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8 bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
          <div className="flex items-center mb-4">
            <Filter className="h-5 w-5 text-slate-400 mr-2" />
            <h2 className="text-lg font-semibold text-white">Filter Guides</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Difficulty Level</label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {difficulties.map((difficulty) => (
                  <option key={difficulty.id} value={difficulty.id}>
                    {difficulty.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results Counter */}
        <div className="mb-6">
          <p className="text-slate-400">
            Showing {filteredGuides.length} guide{filteredGuides.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Guides Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGuides.map((guide, index) => (
            <Link
              key={index}
              to={guide.isLevel ? `/level/${guide.slug}` : `/guides/${guide.slug}`}
              className="group bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all duration-300 card-hover relative"
            >
              {guide.featured && (
                <div className="absolute top-4 right-4">
                  <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    Featured
                  </span>
                </div>
              )}
              
              {guide.isLevel && (
                <div className="mb-4">
                  <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br ${guide.color} rounded-lg mb-2`}>
                    <Award className="h-6 w-6 text-white" />
                  </div>
                </div>
              )}
              
              <div className="mb-4">
                <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${getDifficultyColor(guide.difficulty)}`}>
                  {guide.difficulty.charAt(0).toUpperCase() + guide.difficulty.slice(1)}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-400 transition-colors">
                {guide.title}
              </h3>
              <p className="text-slate-300 mb-4 line-clamp-2">{guide.description}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-slate-400">
                  {guide.isLevel ? (
                    <span className="font-medium text-blue-400">Interactive Guide</span>
                  ) : (
                    <>
                      <Clock className="h-4 w-4 mr-1" />
                      {guide.readTime}
                    </>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-blue-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        {/* No Results */}
        {filteredGuides.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-400 text-lg mb-4">No guides found matching your filters.</p>
            <button
              onClick={() => {
                setSelectedCategory('all')
                setSelectedDifficulty('all')
              }}
              className="text-blue-400 hover:text-blue-300 font-medium"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default GuideListPage